<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table" data-cy="trials-table">
				<thead class="sticky-header">
					<tr>
						<th @click="sortBy('trial_id')" class="sortable-header">
							Trial ID
							<span v-if="sortKey === 'trial_id'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('school_name')" class="sortable-header">
							School Name
							<span v-if="sortKey === 'school_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('district_name')" class="sortable-header">
							District
							<span v-if="sortKey === 'district_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('email')" class="sortable-header">
							Email
							<span v-if="sortKey === 'email'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('created_tstamp')" class="sortable-header">
							Created Date
							<span v-if="sortKey === 'created_tstamp'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('status')" class="sortable-header">
							Status
							<span v-if="sortKey === 'status'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down" }}
								</v-icon>
							</span>
						</th>
						<th class="icon-col" style="text-align: right">
							<v-btn
								@click="reload"
								icon
								color="gray200"
								size="x-small"
								:loading="trialsStore.loading"
								style="max-width: 20px; max-height: 20px"
								data-cy="reload-trials-btn"
							>
								<v-icon>mdi-reload</v-icon>
							</v-btn>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="trial in paginatedResults" :key="trial.trial_id" class="table-row">
						<td>
							<span
								class="trial-id-link"
								@click="editTrial(trial)"
								:data-cy="`trial-id-link-${trial.trial_id}`"
								:title="'Edit ' + trial.trial_id"
							>
								{{ trial.trial_id }}
							</span>
						</td>
						<td>{{ trial.school_name }}</td>
						<td>{{ trial.district_name }}</td>
						<td>{{ trial.email }}</td>
						<td>{{ formatDate(trial.created_tstamp) }}</td>
						<td>
							<v-chip
								:color="getStatusColor(trial.status)"
								size="small"
								class="cursor-pointer"
								@click="openStatusModal(trial)"
								:data-cy="`status-chip-${trial.trial_id}`"
							>
								{{ trial.status }}
							</v-chip>
						</td>
						<td></td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="trialsStore.loading && !trialsStore.trials.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Trials"></LoadingState>
			</div>
			<div
				v-if="!trialsStore.loading && !filteredData.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">No trials found.</div>
					<p>Try adjusting your search or create a new trial.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination
			v-model:curr-page="currPage"
			v-model:per-page="perPage"
			:page-status-text="pageStatusText"
			:total-pages="totalPages"
			@page-down="pageDown"
			@page-up="pageUp"
		></pagination>
	</div>

	<TrialEditModal v-model="showEditModal" :trial="selectedTrial" />
	<TrialStatusModal v-model="showStatusModal" :trial="selectedStatusTrial" />
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref } from "vue";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import dayjs from "dayjs";
import useTrials, { type TrialMembership } from "@/stores/support/trials";
import TrialEditModal from "@/components/support/TrialEditModal.vue";
import TrialStatusModal from "@/components/support/TrialStatusModal.vue";

const props = defineProps<{
  search: string;
  status: string;
}>();

defineEmits<{
  (e: "update:search", ...args: any[]): void;
}>();

const trialsStore = useTrials();

const showEditModal = ref(false);
const selectedTrial = ref<TrialMembership | null>(null);
const showStatusModal = ref(false);
const selectedStatusTrial = ref<TrialMembership | null>(null);

const sortKey = ref<string>("");
const sortOrder = ref<"asc" | "desc">("asc");

onMounted(() => {
  trialsStore.loadTrials({ status: props.status });
});

watch(
  () => props.status,
  (newStatus) => {
    trialsStore.loadTrials({ status: newStatus });
  }
);

/** IDs / subdomain must match the search query exactly (not as a substring). */
const EXACT_MATCH_FIELDS = [
  "trial_id",
  "subdomain",
  "school_id",
  "school_district_id",
  "district_id",
] as const;

const matchesExactId = (trial: TrialMembership, search: string) => {
  const query = search.trim().toLowerCase();
  if (!query) return false;

  return EXACT_MATCH_FIELDS.some((key) => {
    const value = trial[key as keyof TrialMembership];
    if (value == null || value === "") return false;
    return String(value).toLowerCase() === query;
  });
};

const matchesFuzzyFields = (trial: TrialMembership, search: string) => {
  const exactKeys = new Set<string>(EXACT_MATCH_FIELDS);
  const haystack = Object.entries(trial)
    .filter(([key, value]) => !exactKeys.has(key) && value != null && value !== "")
    .map(([, value]) => String(value))
    .join(" ");
  return complexSearch(search, haystack);
};

const filteredData = computed(() => {
  let filtered = trialsStore.trials.filter((trial) => {
    // Trials page: hide pure prospects (lead/demo without a trial date).
    // Prefer trial_date when the API stamps it; until then, request_type is the fallback.
    if (!trial.trial_date) {
      if (trial.request_type === "lead" || trial.request_type === "demo") {
        return false;
      }
    }

    if (props.search) {
      return matchesExactId(trial, props.search) || matchesFuzzyFields(trial, props.search);
    }
    return true;
  });

  if (sortKey.value) {
    filtered = filtered.sort((a, b) => {
      let aValue = a[sortKey.value as keyof TrialMembership] as
        | string
        | number
        | null
        | undefined;
      let bValue = b[sortKey.value as keyof TrialMembership] as
        | string
        | number
        | null
        | undefined;

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder.value === "asc" ? 1 : -1;
      if (bValue == null) return sortOrder.value === "asc" ? -1 : 1;

      if (sortKey.value === "created_tstamp") {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder.value === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder.value === "asc" ? 1 : -1;
      return 0;
    });
  }

  return filtered;
});

const { currPage, perPage, pageStatusText, totalPages, pageDown, pageUp, paginatedResults } =
  usePaginate(filteredData);

const formatDate = (date: string) => dayjs(date).format("MM/DD/YYYY HH:mm");

const getStatusColor = (status: string) => {
  switch (status) {
    case "requested":
      return "info";
    case "fullfilled":
      return "success";
    case "expired":
      return "error";
    case "cancelled":
      return "error";
    case "rejected":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortKey.value = key;
    sortOrder.value = "asc";
  }
};

const reload = () => {
  trialsStore.loadTrials({ status: props.status });
};

const editTrial = (trial: TrialMembership) => {
  selectedTrial.value = { ...trial };
  showEditModal.value = true;
};

const openStatusModal = (trial: TrialMembership) => {
  selectedStatusTrial.value = { ...trial };
  showStatusModal.value = true;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.icon-col {
	min-width: 40px;
}

.sortable-header {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s;

	&:hover {
		background-color: rgba(0, 0, 0, 0.05);
	}
}

.sort-indicator {
	margin-left: 4px;
	display: inline-block;
}

.table-row {
	td {
		transition: background-color 0.3s;
	}
	&:hover {
		td {
			background-color: #f5f5f5;
		}
	}
}

.cursor-pointer {
	cursor: pointer;
}

.trial-id-link {
	font-family: monospace;
	font-size: 0.75rem;
	color: rgb(var(--v-theme-primary));
	cursor: pointer;
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		opacity: 0.75;
	}
}
</style>
